import { useActionButtonContext } from '@aibees/elephant/context/action-button-context';
import { SearchItemUnionProps, SearchType } from '@aibees/elephant/types';
import { Icon } from '@aibees/shared/components';
import { usePopoverPosition } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { CompanyQuickActions } from '../company-quick-actions';
import { PeopleQuickActions } from '../people-quick-actions';
import * as Styled from './styles';
import * as SX from './sx';
import type { ActionButtonProps as Props } from './types';

export const ActionButton = memo(
  ({ className, item, searchType, fullWidth }: Props) => {
    const {
      setItem: setContextItem,
      item: contextItem,
      setSearchTypeOverride,
    } = useActionButtonContext();
    const ref = useRef<HTMLButtonElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const { anchorOrigin, transformOrigin } = usePopoverPosition(anchorEl, {
      thresholdPercentage: 25,
      anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
    });
    const open = Boolean(anchorEl && !!contextItem);

    // to delete previous anchorEl when contextItem is set to undefined in other places
    useEffect(() => {
      if (!contextItem) {
        setAnchorEl(null);
        // Keep the override tied to the open item: the popover often closes
        // programmatically (e.g. TableActionContext.handleClose clears the item
        // after Add to List/Label), which does NOT fire the popover onClose. If we
        // only cleared it there, a stale People/Company override would leak into
        // the shared bulk-action flow.
        setSearchTypeOverride(undefined);
      }
    }, [contextItem, setSearchTypeOverride]);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setContextItem(item as SearchItemUnionProps);
        // Publish this record's search type so the shared action dialogs/mutations
        // key off the record rather than the page route (e.g. a person opened from
        // the employees sidebar on a company search page).
        setSearchTypeOverride(searchType);
        setAnchorEl(event.currentTarget);
      },
      [setContextItem, setSearchTypeOverride, item, searchType]
    );

    const handleClose = useCallback(() => {
      setContextItem(undefined);
      setSearchTypeOverride(undefined);
      setAnchorEl(null);
    }, [setContextItem, setSearchTypeOverride]);

    return (
      <>
        <Styled.Wrapper
          className={classnames('ActionsButton', className)}
          fullWidth={fullWidth}
          variant="outlined"
          onClick={handleClick}
          size="small"
          ref={ref}
          color="default"
          endIcon={
            <Icon name={open ? 'chevron-up' : 'chevron-down'} size={20} />
          }
        >
          Action
        </Styled.Wrapper>

        <Styled.Popover
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          onClose={handleClose}
          sx={SX.popover({
            marginBottom: anchorOrigin.vertical === 'top',
          })}
        >
          {searchType === SearchType.People ? (
            <PeopleQuickActions />
          ) : (
            <CompanyQuickActions />
          )}
        </Styled.Popover>
      </>
    );
  }
);

ActionButton.displayName = 'ActionsButton';
